import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getBestScrollTop, getMainScrollElement, setMainScrollTop } from '@/lib/main-scroll-registry'
import { ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type { TicketListItem } from '@/types/api'

/** Ключ sessionStorage для «Назад» со страницы карточки на список. */
export const TICKETS_LIST_RETURN_URL_KEY = 'ticketsListReturnUrl'

const TICKETS_LIST_SCROLL_BY_URL_KEY = 'sd2_tickets_list_scroll_by_url_v1'

/** Одинаковый ключ при любом порядке query-параметров (иначе restore не находит запись). */
export function normalizeTicketsListScrollUrlKey(pathname: string, search: string): string {
  const raw = search || ''
  const q = raw.startsWith('?') ? raw.slice(1) : raw
  const params = new URLSearchParams(q)
  const entries = Array.from(params.entries()).filter(([, v]) => v !== '')
  entries.sort(([a], [b]) => a.localeCompare(b))
  const next = new URLSearchParams()
  for (const [k, v] of entries) next.append(k, v)
  const s = next.toString()
  return s ? `${pathname}?${s}` : pathname
}

function readScrollByUrlMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(TICKETS_LIST_SCROLL_BY_URL_KEY)
    if (!raw) return {}
    const p = JSON.parse(raw) as unknown
    if (!p || typeof p !== 'object') return {}
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(p as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function writeScrollByUrlMap(m: Record<string, number>) {
  try {
    sessionStorage.setItem(TICKETS_LIST_SCROLL_BY_URL_KEY, JSON.stringify(m))
  } catch {
    /* ignore */
  }
}

function readPendingTicketsListScroll(listUrl: string): number | null {
  const m = readScrollByUrlMap()
  const y = m[listUrl]
  return typeof y === 'number' && Number.isFinite(y) ? y : null
}

function clearPendingTicketsListScroll(listUrl: string) {
  const m = readScrollByUrlMap()
  if (!(listUrl in m)) return
  delete m[listUrl]
  writeScrollByUrlMap(m)
}

function persistScrollForUrlKey(listUrlKey: string) {
  const el = getMainScrollElement()
  if (!el) return
  try {
    const m = readScrollByUrlMap()
    m[listUrlKey] = el.scrollTop
    writeScrollByUrlMap(m)
  } catch {
    /* ignore */
  }
}

/** Пока открыт список — периодически сохраняем scrollTop (не только в момент клика по строке). */
export function usePersistTicketListScrollPosition() {
  const { pathname, search } = useLocation()
  const key = useMemo(() => normalizeTicketsListScrollUrlKey(pathname, search), [pathname, search])

  useEffect(() => {
    let debounceTimer: number
    const onScroll = () => {
      window.clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(() => persistScrollForUrlKey(key), 120)
    }

    let detach: (() => void) | undefined
    let retryId: number | undefined

    const setup = (target: HTMLElement) => {
      target.addEventListener('scroll', onScroll, { passive: true })
      detach = () => {
        window.clearTimeout(debounceTimer)
        target.removeEventListener('scroll', onScroll)
      }
    }

    const el0 = getMainScrollElement()
    if (el0) {
      setup(el0)
    } else {
      retryId = window.setTimeout(() => {
        const el = getMainScrollElement()
        if (el) setup(el)
      }, 0)
    }

    return () => {
      if (retryId != null) window.clearTimeout(retryId)
      detach?.()
    }
  }, [key])
}

/**
 * После загрузки списка восстанавливаем прокрутку области контента (`PageShell`).
 */
export function useRestoreTicketsListScrollWhenReady(
  pathname: string,
  search: string,
  ready: boolean,
) {
  const listUrl = useMemo(() => normalizeTicketsListScrollUrlKey(pathname, search), [pathname, search])

  useLayoutEffect(() => {
    if (!ready) return
    const y = readPendingTicketsListScroll(listUrl)
    if (y == null) return
    setMainScrollTop(y)
  }, [listUrl, ready])

  useEffect(() => {
    if (!ready) return
    const y = readPendingTicketsListScroll(listUrl)
    if (y == null) return

    let cancelled = false

    const tryApply = () => {
      if (cancelled) return
      setMainScrollTop(y)
      const el = getMainScrollElement()
      if (!el) return
      const max = Math.max(0, el.scrollHeight - el.clientHeight)
      if (y > 0 && max < 8) return
      const target = Math.min(y, max)
      if (el.scrollTop >= target - 4) {
        clearPendingTicketsListScroll(listUrl)
      }
    }

    tryApply()
    const timeouts = [0, 24, 72, 160, 320, 600].map((ms) => window.setTimeout(tryApply, ms))
    const done = window.setTimeout(() => {
      if (!cancelled) clearPendingTicketsListScroll(listUrl)
    }, 900)

    const el = getMainScrollElement()
    const ro =
      el && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            tryApply()
          })
        : null
    if (el && ro) ro.observe(el)

    return () => {
      cancelled = true
      timeouts.forEach((id) => window.clearTimeout(id))
      window.clearTimeout(done)
      ro?.disconnect()
    }
  }, [listUrl, ready])
}

/** Открыть карточку обращения во вкладке рабочей области (без дубля URL). */
export function useOpenTicketFromList() {
  const { openOrSelectTab } = useWorkspaceTabs()
  return useCallback(
    (ticket: TicketListItem) => {
      const listUrl = normalizeTicketsListScrollUrlKey(window.location.pathname, window.location.search)
      try {
        sessionStorage.setItem(TICKETS_LIST_RETURN_URL_KEY, listUrl)
        const m = readScrollByUrlMap()
        m[listUrl] = getBestScrollTop()
        writeScrollByUrlMap(m)
      } catch {
        /* ignore */
      }
      const ref = ticketPathSegmentFromListItem(ticket)
      openOrSelectTab(`/tickets/${encodeURIComponent(ref)}`)
    },
    [openOrSelectTab],
  )
}
